import { FC, useEffect, useState, useRef, } from "react";
import { CloseCircleFilled } from "@ant-design/icons";
import { ProCascader } from '../antd';
import { flatCascaderOptions, cascaderValueToValue, dataToOptions } from './utils';
import { CascaderOption, CascaderValueType, SuperCascaderProps } from "./interface";
import { MAX_TAG_TEXT_LENGTH, SUCCESS_STATUS_CODE, TABLE_COLUMN_EMPTY, } from "../constants/constant";
import { arrayAllEqual } from "../utils/dataUtils";
import { NodeKey } from "../constants/interface";

/** 树形分类选择 */
const SuperCascader: FC<SuperCascaderProps> = props => {
  const {
    labelKey, valueKey, childrenKey, cascaderValue, cascaderKey, options, params, disabledStyle, maxLevel,
    dataKey, disabled, isDetail, multiple, childrenLabelKey, childrenValueKey, disabledRender, apiTrigger,
    fetchApi, onChange, onSelect, formatSelectValue, beforeFetch, onFocus, ...restProps
  } = props;
  const [currentValue, setCurrentValue] = useState<NodeKey[]>(); // 当前选中值
  const [cascaderOptions, setCascaderOptions] = useState<CascaderOption[]>(); // 选项配置
  const flatOptions = useRef<CascaderOption[]>(); // 拉平后的cascaderOptions
  const prevParams = useRef<string>();
  const timer = useRef<any>(); // 等待缓存数据定时器
  useEffect(() => {
    if (!apiTrigger) return;
    if (cascaderKey) {
      handleFetch();
      return;
    }
    if (apiTrigger === 'init' || (Array.isArray(apiTrigger) && apiTrigger.includes('init'))) {
      handleFetch();
      return;
    }
  }, [params]);
  useEffect(() => {
    updateValue(cascaderValue);
  }, [cascaderValue]);
  useEffect(() => {
    if (options) setCascaderOptions(options);
  }, [options]);
  useEffect(() => {
    handleFlatOptions(cascaderOptions);
  }, [cascaderOptions]);
  /** 拉平cascaderOptions并设置value值 */
  const handleFlatOptions = (val?: CascaderOption[]) => {
    flatOptions.current = flatCascaderOptions(val);
    updateValue(cascaderValue);
  };
  /** 更新value值 */
  const updateValue = (val?: CascaderValueType) => {
    const newValue = cascaderValueToValue(val, flatOptions.current);
    setCurrentValue(newValue);
  };
  /** 获取数据 */
  const handleFetch = async () => {
    if (!fetchApi) return;
    // 是否从缓存读取
    if (cascaderKey) {
      const CACHE_OPTIONS = window.sessionStorage.getItem(cascaderKey);
      // 有缓存
      if (CACHE_OPTIONS) {
        timer.current && clearInterval(timer.current);
        timer.current = setInterval(() => {
          const resData = window.sessionStorage.getItem(cascaderKey);
          // 判断缓存是否还在加载中
          if (resData && resData !== 'loading') {
            clearInterval(timer.current);
            const resDataObj = JSON.parse(resData);
            setCascaderOptions(resDataObj);
          }
        }, 50);
        return;
      } else {
        // 没有缓存则在缓存中写入正在加载中的标记
        window.sessionStorage.setItem(cascaderKey, 'loading');
      }
    }
    // 不需要读取缓存或者缓存没有数据时通过接口获取
    let apiParams = params;
    if (beforeFetch) {
      apiParams = beforeFetch(params);
      if (apiParams === false) return;
    }
    let apiParamsStr = JSON.stringify(apiParams || {});
    if (prevParams.current && prevParams.current === apiParamsStr) {
      return;
    }
    const res = await fetchApi(apiParams);
    prevParams.current = JSON.stringify(apiParamsStr)
    if (res.code === SUCCESS_STATUS_CODE && res.data) {
      const data = dataKey ? res.data[dataKey] : res.data;
      const newOptions = dataToOptions(data, labelKey, valueKey, childrenKey, 1, childrenLabelKey, childrenValueKey, maxLevel);
      if (cascaderKey) {
        window.sessionStorage.setItem(cascaderKey, JSON.stringify(newOptions));
      }
      setCascaderOptions(newOptions);
    }
  };
  /** 获取焦点 */
  const handleFocus = (event: any) => {
    if (!apiTrigger) return;
    if (!cascaderKey) {
      if ((typeof apiTrigger === 'string' && apiTrigger === 'onFocus') || (Array.isArray(apiTrigger) && apiTrigger.includes('onFocus'))) {
        handleFetch();
      }
    }
    onFocus?.(event);
  };
  /** 清空已选数据 */
  const handleClear = () => {
    setCurrentValue(undefined)
  };
  /** 选择分类改变 */
  const handleChange = (val: any, selectedOptions: any) => {
    if (!multiple && arrayAllEqual(val, currentValue)) return;
    setCurrentValue(val);
    onChange?.(formatSelectValue ? formatSelectValue(val, selectedOptions) : val, selectedOptions);
    if (onSelect) {
      let newValue: any = val;
      let newOption: any = selectedOptions;
      if (multiple) {
        newValue = val?.map?.((item: any) => item?.slice?.(-1)?.[0]);
        newOption = selectedOptions?.map?.((item: any) => item?.slice?.(-1)?.[0]);
      } else {
        newValue = val?.slice?.(-1)?.[0];
        newOption = selectedOptions?.slice?.(-1)?.[0];
      }
      onSelect(newValue, newOption);
    }
  };
  /** 详情表单禁用 */
  const detailRender = () => {
    if (disabledRender) {
      return typeof disabledRender === 'function' ? disabledRender() : disabledRender;
    }
    if (multiple) {
      if (Array.isArray(cascaderValue) && cascaderValue.length) {
        const filterItem = flatOptions.current?.filter(item => cascaderValue.includes(item.value));
        if (filterItem?.length) {
          return filterItem.map(item => item.label).join('、');
        }
      }
      return TABLE_COLUMN_EMPTY;
    }
    const findItem = flatOptions.current?.find(item => item.value === cascaderValue);
    if (!findItem) return TABLE_COLUMN_EMPTY;
    return findItem.cascaderLabel?.join('/');
  };

  return (
    <>
      {
        (isDetail && disabled) ? (
          <div style={ disabledStyle ? undefined : { borderBottom: 'dashed 1px #adb5bd' }}>
            {detailRender()}
          </div>
        ) : (
          <ProCascader
            {...restProps}
            multiple={multiple}
            disabled={disabled}
            value={currentValue}
            options={cascaderOptions}
            clearIcon={<CloseCircleFilled onClick={handleClear} />}
            onChange={handleChange}
            onFocus={handleFocus}
          />
        )
      }
    </>
  );
};

SuperCascader.defaultProps = {
  labelKey: 'label',
  valueKey: 'value',
  childrenKey: 'children',
  placeholder: '请选择',
  showSearch: true,
  maxTagCount: 1,
  expandTrigger: 'hover',
  apiTrigger: 'init',
  maxTagTextLength: MAX_TAG_TEXT_LENGTH,
  maxTagPlaceholder: (omittedValues: any) => {
    return `+${omittedValues.length}`;
  },
};

export default SuperCascader;
